import { useEffect, useMemo, useState } from "react";
import { Check, Pencil, Trash2 } from "lucide-react";
import AddButton from "@/components/Core/AddButton/AddButton";
import Button from "@/components/Core/Button/Button";
import KnowledgeBaseTrigger from "@/components/Shared/KnowledgeBase/KnowledgeBaseTrigger";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { CompetenceItem } from "@/models/Competence";
import { CompetenceRequirement, RequirementRef } from "@/models/Organisation";
import { useOrganisation, useUpdateOrganisation } from "@/networking/organisation.hooks";
import { filterArchivedCompetences, isArchivedCompetencePath } from "@/utils/competence.utils";
import { SettingsLeaf } from "./SettingsLeafLayout";

const refId = (ref: RequirementRef) => `${ref.kind}:${ref.key}`;

type RuleScope = Pick<CompetenceRequirement, "branch" | "service" | "skill" | "customerTag">;

/**
 * Scope of a rule, encoded "branch[|service[|skill]][#customerTag]". The
 * competence part and the customer-tag part are both optional — the UI offers
 * one or the other, but combined rules (created via API) round-trip intact.
 */
const scopeOf = (rule: RuleScope) => {
	const competencePath = [rule.branch, rule.service, rule.skill].filter(Boolean).join("|");
	return rule.customerTag ? `${competencePath}#${rule.customerTag}` : competencePath;
};

const scopeToRule = (scope: string): RuleScope => {
	const [competencePath, customerTag] = scope.split("#");
	const [branch, service, skill] = competencePath.split("|");
	return {
		...(branch ? { branch } : {}),
		...(service ? { service } : {}),
		...(skill ? { skill } : {}),
		...(customerTag ? { customerTag } : {}),
	};
};

/** The two independently editable parts of a scope string. */
const splitScope = (scope: string): { competencePath: string; customerTag: string } => {
	const [competencePath = "", customerTag = ""] = scope.split("#");
	return { competencePath, customerTag };
};

const joinScope = (competencePath: string, customerTag: string) =>
	customerTag ? `${competencePath}#${customerTag}` : competencePath;

/**
 * Sentinel select value for the unscoped state ("alla uppdrag" / "alla
 * kunder") — Radix Select items cannot carry an empty string value.
 */
const NO_SCOPE = "*";

/**
 * One editor row = one sentence: "För <uppdrag inom scope / kunder med tagg>
 * måste konsulten ha <certifikat/tagg>". The stored model allows several
 * requirements per rule, so rows are exploded from `requires` on load and
 * collapsed back (grouped by scope, deduplicated) on save — the backend shape
 * is unchanged.
 */
interface RuleRow {
	scope: string;
	ref?: RequirementRef;
	/** UI-only: the row shows inline selects instead of the read-only sentence. */
	editing?: boolean;
}

/**
 * "Matchningsregler" — editor for `organisation.competenceRequirements`: the
 * certifications and consultant tags a consultant must hold — valid through
 * the job's end — to match jobs within a verksamhetsområde or tjänst, for
 * customers carrying a kundtagg, or both combined (all conditions must hold).
 * Superseded the separate Poliskontroll-krav page: a belastningsutdrag
 * requirement is expressed as a certification requirement here. Saved via the
 * organisation update PUT as a whole-array replace (draft + save button).
 */
const MatchningsreglerPage = () => {
	const { data: organisation } = useOrganisation();
	const { mutate: updateOrganisation, isLoading: isSaving } = useUpdateOrganisation();

	const [rows, setRows] = useState<RuleRow[]>([]);

	useEffect(() => {
		setRows(
			(organisation?.competenceRequirements ?? []).flatMap((rule) =>
				rule.requires.map((ref): RuleRow => ({ scope: scopeOf(rule), ref }))
			)
		);
	}, [organisation?.competenceRequirements]);

	/** The raw tree (archived included) — used to resolve labels for existing rules. */
	const allBranches: CompetenceItem[] = organisation?.competences ?? [];

	/** Only active competences are offered when creating/editing a rule. */
	const branches: CompetenceItem[] = useMemo(
		() => filterArchivedCompetences(allBranches),
		[allBranches]
	);

	/** Rules keyed on archived competences are dormant — flagged, never deleted. */
	const scopeIsArchived = (scope: string) => {
		const [competencePath] = scope.split("#");

		if (!competencePath) return false;

		const [branch, service, skill] = competencePath.split("|");
		return isArchivedCompetencePath(allBranches, branch, service, skill);
	};

	const customerTagLabel = (tagKey: string) =>
		organisation?.customerTagTypes?.find(({ key }) => key === tagKey)?.name.sv ?? tagKey;

	/**
	 * Resolve a scope label from the raw tree/taxonomy — fallback for scopes
	 * not among the offered options (archived competences, combined rules).
	 */
	const scopeLabelFromTree = (scope: string) => {
		const [competencePath, customerTag] = scope.split("#");
		const [branchKey, serviceKey] = competencePath.split("|");
		const branch = allBranches.find(({ key }) => key === branchKey);
		const service = branch?.items?.find(({ key }) => key === serviceKey);

		const parts: string[] = [];

		if (branchKey)
			parts.push(
				"uppdrag inom " +
					[
						branch?.name.sv ?? branchKey,
						...(serviceKey ? [service?.name.sv ?? serviceKey] : []),
					].join(" – ")
			);

		if (customerTag) parts.push(`kunder med taggen ${customerTagLabel(customerTag)}`);

		return parts.join(" hos ");
	};

	/**
	 * Competence-part options ("uppdrag inom …"): active branches and
	 * branch–tjänst pairs, plus any legacy/archived competence path present in
	 * rows so existing rules stay editable without being wiped.
	 */
	const competenceScopeOptions: { key: string; label: string }[] = useMemo(() => {
		const options: { key: string; label: string }[] = [];

		branches.forEach((branch) => {
			options.push({ key: branch.key, label: `uppdrag inom ${branch.name.sv ?? branch.key}` });
			(branch.items ?? []).forEach((service) =>
				options.push({
					key: `${branch.key}|${service.key}`,
					label: `uppdrag inom ${branch.name.sv ?? branch.key} – ${service.name.sv ?? service.key}`,
				})
			);
		});

		const known = new Set(options.map((option) => option.key));

		rows.forEach((row) => {
			const { competencePath } = splitScope(row.scope);
			if (competencePath && !known.has(competencePath)) {
				known.add(competencePath);
				options.push({ key: competencePath, label: scopeLabelFromTree(competencePath) });
			}
		});

		return options;
	}, [branches, rows]);

	/**
	 * Customer-tag-part options ("kunder med taggen …"), plus any tag key
	 * present in rows but missing from the taxonomy (concurrently deleted —
	 * the backend cascade normally removes such rules).
	 */
	const customerTagScopeOptions: { key: string; label: string }[] = useMemo(() => {
		const options = (organisation?.customerTagTypes ?? []).map((tag) => ({
			key: tag.key,
			label: `kunder med taggen ${tag.name.sv ?? tag.key}`,
		}));

		const known = new Set(options.map((option) => option.key));

		rows.forEach((row) => {
			const { customerTag } = splitScope(row.scope);
			if (customerTag && !known.has(customerTag)) {
				known.add(customerTag);
				options.push({
					key: customerTag,
					label: `kunder med taggen ${customerTagLabel(customerTag)}`,
				});
			}
		});

		return options;
	}, [organisation?.customerTagTypes, rows]);

	const requirementOptions: { key: string; label: string }[] = useMemo(
		() => [
			...(organisation?.certificationTypes ?? []).map((c) => ({
				key: `certification:${c.key}`,
				label: `Certifikat: ${c.name.sv}`,
			})),
			...(organisation?.consultantTagTypes ?? []).map((tag) => ({
				key: `tag:${tag.key}`,
				label: `Tagg: ${tag.name.sv}`,
			})),
		],
		[organisation?.certificationTypes, organisation?.consultantTagTypes]
	);

	const updateRow = (index: number, patch: Partial<RuleRow>) =>
		setRows(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));

	const removeRow = (index: number) => setRows(rows.filter((_, i) => i !== index));

	const addRow = () => setRows([...rows, { scope: "", editing: true }]);

	/** Requirements already used for the same scope in other rows are hidden. */
	const availableRequirementOptions = (index: number) => {
		const row = rows[index];
		const usedInScope = new Set(
			rows
				.filter((other, i) => i !== index && other.scope === row.scope && other.ref)
				.map((other) => refId(other.ref!))
		);
		return requirementOptions.filter((option) => !usedInScope.has(option.key));
	};

	// Incomplete rows are inert and filtered out on save; complete rows are
	// grouped back into one rule per scope (deduplicated), so the stored shape
	// round-trips unchanged.
	const buildPayload = (): CompetenceRequirement[] => {
		const byScope = new Map<string, CompetenceRequirement>();

		rows.forEach((row) => {
			if (!row.scope || !row.ref) return;

			const existing = byScope.get(row.scope);

			if (!existing) {
				byScope.set(row.scope, { ...scopeToRule(row.scope), requires: [row.ref] });
			} else if (!existing.requires.some((ref) => refId(ref) === refId(row.ref!))) {
				existing.requires.push(row.ref);
			}
		});

		return Array.from(byScope.values());
	};

	const hasUnsavedChanges =
		JSON.stringify(buildPayload()) !== JSON.stringify(organisation?.competenceRequirements ?? []);

	const saveRules = () => updateOrganisation({ competenceRequirements: buildPayload() });

	// The read-only sentence part — scopeLabelFromTree composes all scope
	// shapes ("uppdrag inom X", "kunder med taggen Y" and combinations).
	const scopeLabel = scopeLabelFromTree;

	const refLabel = (ref: RequirementRef) =>
		requirementOptions.find((option) => option.key === refId(ref))?.label ?? ref.key;

	return (
		<SettingsLeaf
			wide
			title={
				<span className="inline-flex items-center gap-1.5">
					Matchningsregler
					<KnowledgeBaseTrigger tag="matchningsregler" />
				</span>
			}
			description="Regler för vilka certifikat och/eller taggar en konsult måste ha för att matchas mot uppdrag inom en bransch eller tjänst, mot uppdrag hos kunder med en viss kundtagg — eller båda i kombination."
		>
			{branches.length === 0 && (organisation?.customerTagTypes ?? []).length === 0 ? (
				<p className="text-sm text-gray-500">
					Inga tjänster/kompetenser eller kundtaggar finns att ställa krav på ännu.
				</p>
			) : (
				<>
					{requirementOptions.length === 0 && (
						<p className="text-sm text-gray-500">
							Skapa certifikat eller konsulttaggar först för att kunna välja krav.
						</p>
					)}

					{rows.length === 0 ? (
						<p className="text-sm text-gray-500">Inga matchningsregler har skapats ännu.</p>
					) : (
						<div className="divide-y divide-gray-100 rounded-md border border-gray-200">
							{rows.map((row, index) => (
								<div key={index} className="flex items-center gap-x-2 px-4 py-2.5">
									{row.editing ? (
										<>
											<span className="shrink-0 text-sm text-gray-700">För</span>
											<Select
												value={splitScope(row.scope).competencePath || NO_SCOPE}
												onValueChange={(value) =>
													updateRow(index, {
														scope: joinScope(
															value === NO_SCOPE ? "" : value,
															splitScope(row.scope).customerTag
														),
													})
												}
											>
												<SelectTrigger className="h-8 w-auto min-w-[140px] gap-1.5 text-sm">
													<SelectValue placeholder="Välj område..." />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value={NO_SCOPE} className="text-sm">
														alla uppdrag
													</SelectItem>
													{competenceScopeOptions.map((option) => (
														<SelectItem key={option.key} value={option.key} className="text-sm">
															{option.label}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
											<span className="shrink-0 text-sm text-gray-700">hos</span>
											<Select
												value={splitScope(row.scope).customerTag || NO_SCOPE}
												onValueChange={(value) =>
													updateRow(index, {
														scope: joinScope(
															splitScope(row.scope).competencePath,
															value === NO_SCOPE ? "" : value
														),
													})
												}
											>
												<SelectTrigger className="h-8 w-auto min-w-[140px] gap-1.5 text-sm">
													<SelectValue placeholder="Välj kundtagg..." />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value={NO_SCOPE} className="text-sm">
														alla kunder
													</SelectItem>
													{customerTagScopeOptions.map((option) => (
														<SelectItem key={option.key} value={option.key} className="text-sm">
															{option.label}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
											<span className="shrink-0 text-sm text-gray-700">måste konsulten ha</span>
											<Select
												value={row.ref ? refId(row.ref) : ""}
												onValueChange={(value) => {
													const [kind, ...rest] = value.split(":");
													updateRow(index, {
														ref: { kind: kind as RequirementRef["kind"], key: rest.join(":") },
													});
												}}
											>
												<SelectTrigger className="h-8 w-auto min-w-[200px] gap-1.5 text-sm">
													<SelectValue placeholder="Välj certifikat eller tagg..." />
												</SelectTrigger>
												<SelectContent>
													{availableRequirementOptions(index).map((option) => (
														<SelectItem key={option.key} value={option.key} className="text-sm">
															{option.label}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
											{!row.scope && (
												<span className="shrink-0 text-xs font-medium text-amber-700">
													Välj minst område eller kundtagg
												</span>
											)}
										</>
									) : (
										<span className="truncate text-sm text-gray-700">
											För{" "}
											<span
												className="font-medium text-blue-500 underline decoration-dashed cursor-pointer"
												onClick={() => updateRow(index, { editing: !row.editing })}
											>
												{row.scope ? scopeLabel(row.scope) : "…"}
											</span>{" "}
											måste konsulten ha{" "}
											<span
												className="font-medium text-blue-500 underline decoration-dashed cursor-pointer"
												onClick={() => updateRow(index, { editing: !row.editing })}
											>
												{row.ref ? refLabel(row.ref) : "…"}
											</span>
											{row.scope && scopeIsArchived(row.scope) && (
												<span
													className="ml-2 inline-block shrink-0 rounded bg-amber-50 px-1.5 py-0.5 text-xs font-medium text-amber-700"
													title="Regeln är vilande: kompetensen är arkiverad och kan inte väljas för nya uppdrag. Regeln aktiveras igen om kompetensen återställs."
												>
													Gäller arkiverad kompetens
												</span>
											)}
										</span>
									)}
									<div className="ml-auto flex shrink-0 items-center gap-1">
										<button
											type="button"
											onClick={() => updateRow(index, { editing: !row.editing })}
											aria-label={row.editing ? "Klar" : "Redigera regel"}
											title={row.editing ? "Klar" : "Redigera regel"}
											className="flex h-8 w-8 items-center justify-center rounded-sm border border-transparent text-gray-400 transition-colors hover:border-gray-200 hover:bg-gray-50 hover:text-gray-700"
										>
											{row.editing ? <Check size={15} /> : <Pencil size={14} />}
										</button>
										<button
											type="button"
											onClick={() => removeRow(index)}
											aria-label="Ta bort regel"
											title="Ta bort regel"
											className="flex h-8 w-8 items-center justify-center rounded-sm border border-transparent text-gray-400 transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-600"
										>
											<Trash2 size={15} />
										</button>
									</div>
								</div>
							))}
						</div>
					)}

					<div>
						<AddButton label="Lägg till regel" onClick={addRow} />
					</div>

					<div>
						<Button
							onClick={saveRules}
							disabled={!hasUnsavedChanges || isSaving}
							height="37px"
							fontSize="s"
						>
							Spara ändringar
						</Button>
					</div>
				</>
			)}
		</SettingsLeaf>
	);
};

export default MatchningsreglerPage;
